import { DeleteObjectsCommand, PutObjectCommand, type S3Client } from '@aws-sdk/client-s3'
import {
  copyObject,
  getJsonObject,
  getRequiredJsonObject,
  paginateList,
  UPLOAD_BUCKET,
  withS3Client,
  type S3Io,
} from './s3'
import {
  appendToyToIndex,
  loadToyIndex,
  saveToyIndex,
  type ToyIndexEntry,
} from './toyIndex'
import {
  getVersionsIndex,
  listVersionFolders,
  maxSemver,
  putVersionsIndex,
  resolveLiveVersion,
} from './toyVersions'

const METADATA_FILENAME = 'metadata.json'
const DELETE_BATCH_SIZE = 1000

// The "Featured Apps" catalog lives under a reserved identityId. Toys copied
// here surface in the Featured section. This id is not a real Cognito identity.
export const FEATURED_IDENTITY_ID = '16d93e22-e06c-418e-92fd-a0c606758566'

export async function listUserFolders(io: S3Io): Promise<string[]> {
  return withS3Client(io, async (client) => {
    const folders: string[] = []
    for await (const res of paginateList(client, {
      Bucket: UPLOAD_BUCKET,
      Delimiter: '/',
    })) {
      for (const p of res.CommonPrefixes ?? []) {
        if (p.Prefix) folders.push(p.Prefix.replace(/\/$/, ''))
      }
    }
    return folders
  })
}


// Lists and batch-deletes every object under a prefix, returning the deleted
// keys.
async function deleteAllUnderPrefix(client: S3Client, prefix: string): Promise<string[]> {
  const keys: string[] = []
  for await (const res of paginateList(client, { Bucket: UPLOAD_BUCKET, Prefix: prefix })) {
    for (const o of res.Contents ?? []) {
      if (o.Key) keys.push(o.Key)
    }
  }
  for (let i = 0; i < keys.length; i += DELETE_BATCH_SIZE) {
    const batch = keys.slice(i, i + DELETE_BATCH_SIZE)
    await client.send(
      new DeleteObjectsCommand({
        Bucket: UPLOAD_BUCKET,
        Delete: { Objects: batch.map((Key) => ({ Key })), Quiet: true },
      }),
    )
  }
  return keys
}

export async function deleteToy(
  io: S3Io,
  identityId: string,
  toyId: string,
): Promise<{ deletedKeys: number }> {
  const keys = await withS3Client(io, (client) =>
    deleteAllUnderPrefix(client, `${identityId}/${toyId}/`),
  )

  // NOTE: load-then-save is not atomic — a concurrent upload from the user
  // could race with this rewrite. For a rarely-used admin tool against a
  // single-user index this is acceptable; revisit if contention becomes real.
  const index = await loadToyIndex(io, identityId)
  const filtered = index.filter((e) => e.id !== toyId)
  if (filtered.length !== index.length) {
    await saveToyIndex(io, filtered, identityId)
  }
  return { deletedKeys: keys.length }
}

// Deletes one version folder of a toy and drops its versions-index entry.
// The live version is protected: it's what toy-index.json and the install
// links serve, so it must be superseded (promote another version) before it
// can be deleted. toy-index.json is untouched — non-live versions aren't in it.
export async function deleteToyVersion(
  io: S3Io,
  identityId: string,
  toyId: string,
  version: string,
): Promise<{ deletedKeys: number }> {
  return withS3Client(io, async (client) => {
    const index = await getVersionsIndex(client, identityId, toyId)
    const folders = await listVersionFolders(client, identityId, toyId)
    if (version === resolveLiveVersion(index, folders)) {
      throw new Error(`Version ${version} is live — promote another version first`)
    }

    const keys = await deleteAllUnderPrefix(client, `${identityId}/${toyId}/${version}/`)

    if (index && index.versions.some((v) => v.version === version)) {
      await putVersionsIndex(client, identityId, toyId, {
        ...index,
        versions: index.versions.filter((v) => v.version !== version),
      })
    }
    return { deletedKeys: keys.length }
  })
}

function rewriteMetadataForFeatured(
  metadata: ToyIndexEntry,
  sourceIdentityId: string,
): ToyIndexEntry {
  // Attribution (creatorId / creatorName) stays with the original author.
  // Only rewrite URL fields that embed the source identity id, so they point
  // at the Featured copy of the object rather than the source user's folder.
  const copy: ToyIndexEntry = { ...metadata }
  for (const key of ['manifestUrl', 'thumbnailUrl'] as const) {
    const v = metadata[key]
    if (typeof v === 'string' && v.includes(sourceIdentityId)) {
      copy[key] = v.split(sourceIdentityId).join(FEATURED_IDENTITY_ID)
    }
  }
  return copy
}

const COPY_CONCURRENCY = 8

// Publishes ONE version of a user's toy into the Featured Apps folder, under
// the same version number (refusing collisions — featured can't mint its own
// versions, so numbers stay in the source's timeline). The copy lands dormant:
// featured's versions-index latest is unchanged and the admin promotes it
// there explicitly. Exception: the first-ever copy of a toy goes live
// immediately, since featured has nothing else to serve.
export async function copyVersionToFeatured(
  io: S3Io,
  sourceIdentityId: string,
  toyId: string,
  version: string,
): Promise<{ copiedKeys: number; live: boolean }> {
  if (sourceIdentityId === FEATURED_IDENTITY_ID) {
    throw new Error('Source is already the Featured Apps folder')
  }
  const sourcePrefix = `${sourceIdentityId}/${toyId}/${version}/`
  const destPrefix = `${FEATURED_IDENTITY_ID}/${toyId}/${version}/`

  const result = await withS3Client(io, async (client) => {
    // Validate the source version before writing anything.
    const metadata = (await getRequiredJsonObject(
      client,
      UPLOAD_BUCKET,
      `${sourcePrefix}${METADATA_FILENAME}`,
      `Version ${version} ${METADATA_FILENAME}`,
    )) as ToyIndexEntry
    if (metadata.version !== version) {
      throw new Error(
        `metadata.json says version ${JSON.stringify(metadata.version)} but the folder is ${version}`,
      )
    }

    const featuredVersions = await listVersionFolders(client, FEATURED_IDENTITY_ID, toyId)
    if (featuredVersions.includes(version)) {
      throw new Error(
        `Version ${version} already exists in Featured — delete it there first if a re-copy is intended`,
      )
    }
    // A featured toy from the pre-versioning era serves its legacy root, and
    // "dormant" isn't representable next to a legacy root (any version folder
    // outranks it in the resolution order), so a copied version would take
    // over silently. Refuse instead of surprising anyone.
    if (featuredVersions.length === 0) {
      const legacyRoot = await getJsonObject(
        client,
        UPLOAD_BUCKET,
        `${FEATURED_IDENTITY_ID}/${toyId}/${METADATA_FILENAME}`,
      )
      if (legacyRoot.kind !== 'missing') {
        throw new Error(
          'Featured has a legacy un-versioned copy of this toy — delete it in Featured first, then copy the version again',
        )
      }
    }

    // Copy the one version folder, nothing else.
    const keys: string[] = []
    for await (const res of paginateList(client, {
      Bucket: UPLOAD_BUCKET,
      Prefix: sourcePrefix,
    })) {
      for (const o of res.Contents ?? []) {
        if (o.Key) keys.push(o.Key)
      }
    }
    for (let i = 0; i < keys.length; i += COPY_CONCURRENCY) {
      await Promise.all(
        keys.slice(i, i + COPY_CONCURRENCY).map((sourceKey) =>
          copyObject(
            client,
            UPLOAD_BUCKET,
            sourceKey,
            destPrefix + sourceKey.slice(sourcePrefix.length),
          ),
        ),
      )
    }

    // Rewrite the copied metadata so its URLs point at the featured copy
    // (attribution stays with the original creator).
    const rewritten = rewriteMetadataForFeatured(metadata, sourceIdentityId)
    await client.send(
      new PutObjectCommand({
        Bucket: UPLOAD_BUCKET,
        Key: `${destPrefix}${METADATA_FILENAME}`,
        Body: new TextEncoder().encode(JSON.stringify(rewritten, null, 2)),
        ContentType: 'application/json',
      }),
    )

    // Featured versions-index: first-ever copy goes live; later copies are
    // appended dormant with latest untouched.
    const entry = {
      version,
      createdAt:
        typeof metadata.creationTime === 'string'
          ? metadata.creationTime
          : new Date().toISOString(),
    }
    const live = featuredVersions.length === 0
    if (live) {
      await putVersionsIndex(client, FEATURED_IDENTITY_ID, toyId, {
        schemaVersion: 1,
        latest: version,
        versions: [entry],
      })
    } else {
      const index = await getVersionsIndex(client, FEATURED_IDENTITY_ID, toyId)
      if (index) {
        await putVersionsIndex(client, FEATURED_IDENTITY_ID, toyId, {
          ...index,
          versions: [...index.versions, entry],
        })
      } else {
        // Folders exist but the index is missing: reconstruct it with the
        // highest existing folder as latest, so this copy still lands dormant.
        await putVersionsIndex(client, FEATURED_IDENTITY_ID, toyId, {
          schemaVersion: 1,
          latest: maxSemver(featuredVersions)!,
          versions: [...featuredVersions.map((v) => ({ version: v })), entry],
        })
      }
    }
    return { copiedKeys: keys.length, live, rewritten }
  })

  // A live first copy is also the featured catalog entry.
  if (result.live) {
    await appendToyToIndex(io, FEATURED_IDENTITY_ID, result.rewritten)
  }
  return { copiedKeys: result.copiedKeys, live: result.live }
}
